import React, { useState } from "react";
import { Box, Typography, Paper, Grid, Card, CardContent, Button } from "@mui/material";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";

const LeibnizScreen: React.FC = () => {
    const rules = [
        {
            title: "The Power Rule",
            description: "If $f(x) = x^n$, then the derivative is:",
            label: "Power Rule",
            math: "\\frac{d}{dx}(x^n) = nx^{n-1}"
        },
        {
            title: "The Product Rule",
            description: "If $h(x) = f(x)g(x)$, then:",
            label: "Product Rule",
            math: "\\frac{d}{dx}[f(x)g(x)] = f(x)\\frac{dg}{dx} + g(x)\\frac{df}{dx}"
        },
        {
            title: "The Quotient Rule",
            description: "If $h(x) = \\frac{f(x)}{g(x)}$, then:",
            label: "Quotient Rule",
            math: "\\frac{d}{dx}\\left[\\frac{f(x)}{g(x)}\\right] = \\frac{g(x)\\frac{df}{dx} - f(x)\\frac{dg}{dx}}{[g(x)]^2}"
        },
        {
            title: "The Chain Rule",
            description: "If $y = f(u)$ and $u = g(x)$, then:",
            label: "Chain Rule",
            math: "\\frac{dy}{dx} = \\frac{dy}{du} \\cdot \\frac{du}{dx}"
        }
    ];

    const [activeRule, setActiveRule] = useState(rules[0]);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", color: "secondary.main" }}>
                Leibniz Notation & Calculus
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
                Gottfried Wilhelm Leibniz's notation is widely used in calculus for derivatives and integrals.
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {rules.map((rule, index) => (
                            <Button
                                key={index}
                                variant={activeRule.title === rule.title ? "contained" : "outlined"}
                                color="secondary"
                                onClick={() => setActiveRule(rule)}
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 2 }}
                            >
                                {rule.label}
                            </Button>
                        ))}
                    </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card elevation={4} sx={{ borderRadius: 4, minHeight: '350px' }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {activeRule.title}
                            </Typography>
                            <Box sx={{ my: 3 }}>
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {activeRule.description}
                                </Markdown>
                            </Box>

                            <Box sx={{
                                p: 4,
                                bgcolor: 'action.hover',
                                borderRadius: 3,
                                display: 'flex',
                                justifyContent: 'center',
                                fontSize: '2rem'
                            }}>
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {`$$ ${activeRule.math} $$`}
                                </Markdown>
                            </Box>

                            <Box sx={{ mt: 3, p: 2, borderLeft: '4px solid', borderColor: 'secondary.main', bgcolor: 'secondary.light', opacity: 0.8 }}>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    Notice the use of $d/dx$ or $dy/dx$ notation, which emphasizes the derivative as a ratio of infinitesimals.
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box sx={{ mt: 5 }}>
                <Typography variant="h6" gutterBottom>Integration with Leibniz Notation</Typography>
                <Paper sx={{ p: 3, bgcolor: '#f4f6f8' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="body1">
                                The integral sign $\int$ was also introduced by Leibniz as an elongated 'S' for 'summa'.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ fontSize: '1.5rem', textAlign: 'center' }}>
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {"$$ \\int f(x) \\, dx = F(x) + C $$"}
                                </Markdown>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </Box>
    );
};

export default LeibnizScreen;
